import Immutable from "immutable";
import exactMath from "exact-math";
import { isDatePast } from "lib/date";
import { aggregateID } from "lib/id";

import {
  getBasketItemByID,
  doesBasketItemExist,
  doesBasketItemGroupExist,
  getBasketItemsOfType
} from "state/school_money/selectors";

/**
 * @returns {Immutable.List}
 */
export function getPaymentIDList(state) {
  return state.getIn(["school_money", "otherPayments"]).keySeq().toList();
}

/**
 * @returns {boolean}
 */
export function isPaymentVisible(state, paymentID) {
  return !isPaymentOutOfPlaces(state, paymentID);
}

/**
 * @returns {Immutable.List}
 */
export function getVisiblePaymentIDList(state) {
  return getPaymentIDList(state)
    .filter(paymentID => isPaymentVisible(state, paymentID));
}

/**
 * @returns {boolean}
 */
export function doesPaymentExist(state, paymentID) {
  if(!paymentID) { return false; }
  const payments = getPaymentIDList(state);
  return payments.includes(paymentID);
}

/**
 * @returns {Immutable.List}
 */
export function getStudentPaymentIDList(state, studentID) {
  return state.getIn(["school_money", "otherPayments"])
    .filter(payment => payment.get("studentID") === studentID)
    .keySeq().toList()
    .filter(paymentID => isPaymentVisible(state, paymentID))
}

/**
 * @returns {Immutable.Map}
 */
export function getPaymentByID(state, paymentID) {
  return state.getIn(["school_money", "otherPayments", paymentID]);
}

/**
 * @returns {number}
 */
export function getPaymentTotalOutstandingPrice(state, paymentID) {
  const payment = getPaymentByID(state, paymentID);
  return payment.installments
    .map(installment => installment.outstandingPrice)
    .reduce((totalPrice, installmentPrice) => exactMath.add(totalPrice, installmentPrice), 0);
}

/**
 * @returns {Immutable.Map}
 */
export function getPaymentInstallment(state, paymentID, installmentNumber) {
  const payment = getPaymentByID(state, paymentID);
  return payment.get("installments").get(installmentNumber);
}

/**
 * @returns {number}
 */
export function getPaymentInstallmentSelectedPrice(state, paymentID, installmentNumber) {
  const isInBasket = isPaymentInstallmentInBasket(state, paymentID, installmentNumber);
  if (isInBasket) {
    const basketItem = getBasketItemByID(state, "payment", aggregateID(paymentID, installmentNumber));
    return basketItem.price;
  } else {
    const installment = getPaymentInstallment(state, paymentID, installmentNumber);
    return installment.selectedPrice;
  }
}

/**
 * @returns {boolean}
 */
export function isPaymentInstallmentOverdue(state, paymentID, installmentNumber) {
  const installment = getPaymentInstallment(state, paymentID, installmentNumber);
  return isDatePast(installment.get("dueDate"));
}

/**
 * @returns {boolean}
 */
export function isPaymentOverdue(state, paymentID) {
  const payment = getPaymentByID(state, paymentID);
  const installments = payment.get("installments");
  return installments.some(installment => isDatePast(installment.get("dueDate")));
}

/**
 * @returns {boolean}
 */
export function isPaymentInstallmentInBasket(state, paymentID, installmentNumber) {
  return doesBasketItemExist(state, "payment", aggregateID(paymentID, installmentNumber));
}

/**
 * @returns {boolean}
 */
export function isPaymentInBasket(state, paymentID) {
  return doesBasketItemGroupExist(state, "payment", paymentID);
}

/**
 * @returns {boolean}
 */
export function isPaymentExpanded(state, paymentID) {
  return state.getIn(["school_money", "expandedPaymentID"]) === paymentID;
}

/**
 * @returns {boolean}
 */
export function isPaymentSeen(state, paymentID) {
  return state.getIn(["school_money", "seenOtherPaymentIDs"]).includes(paymentID);
}

/**
 * @returns {number}
 */
export function getNewPaymentCount(state) {
  const paymentIDList = getPaymentIDList(state);
  return paymentIDList
    .filterNot(paymentID => isPaymentSeen(state, paymentID))
    .filter(paymentID => !isPaymentOutOfPlaces(state, paymentID))
    .count();
}

/**
 * @returns {boolean}
 */
export function isPaymentsReady(state) {
  return !state.getIn(["school_money", "isActivePaymentsLoading"]);
}

/**
 * @returns {Immutable.List}
 */
export function getOverduePayments(state) {
  const paymentIDList = getVisiblePaymentIDList(state);
  return paymentIDList
    .filter(paymentID => isPaymentOverdue(state, paymentID))
    .map(paymentID => getPaymentByID(state, paymentID))
    .map(payment => Immutable.Map({
      paymentID: payment.get("id"),
      studentID: payment.get("studentID"),
      description: payment.get("description"),
      dueDate: payment.get("installments")
        .map(installment => installment.get("dueDate"))
        .sort()
        .first()
    }))
    .sortBy(overduePayment => overduePayment.get("dueDate"))
}

/**
 * Returns true if a limited places payment has been filled
 *   and the student is not occupying one of the places.
 * @returns {boolean}
 */
export function isPaymentOutOfPlaces(state, paymentID) {
  const { hasLimitedPlaces, placesAvailable, isPlaceReserved } = getPaymentByID(state, paymentID);
  const isFull = placesAvailable <= 0;
  return hasLimitedPlaces && isFull && !isPlaceReserved;
}

/**
 * True if the given payment requires parental consent
 *   and it is not yet given.
 * If a payment is in basket, that counts as consent
 *   given (even though db is not updated yet).
 * @returns {boolean}
 */
export function isPaymentPendingParentalConsent(state, paymentID) {
  const { requiresParentalConsent, isParentalConsentGiven } = getPaymentByID(state, paymentID);
  const isInBasket = isPaymentInBasket(state, paymentID);
  return requiresParentalConsent && !isParentalConsentGiven && !isInBasket;
}

/**
 * @returns {Object} { enabled, total, taken }
 */
export function getPaymentLimitedPlacesDetails(state, paymentID) {
  const payment = getPaymentByID(state, paymentID);

  // Get list of students who have has this payment in basket
  const studentsWithBooking = getBasketItemsOfType(state, "payment")
    .filter( basketPayment => basketPayment.getIn(["payload", "payment", "remotePaymentID"]) === payment.remotePaymentID)
    .map(basketItem => basketItem.getIn(["payload", "payment", "remoteStudentID"]));

  return {
    enabled: payment.hasLimitedPlaces,
    total: payment.placesTotal,
    available: payment.placesAvailable - studentsWithBooking.size,
    isPlaceReserved: payment.isPlaceReserved
  }
}

/**
 * @returns {string}
 */
export function getGoToPaymentID(state) {
  return state.getIn(["school_money", "goToPaymentID"]);
}



// WEBPACK FOOTER //
// ./src/state/school_money/selectors/payments.js